import { Component, createRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import getComponentName from 'react-utils/getComponentName';
import styled from 'styled-components';
import PortalIdParser from 'PortalIdParser';
import { animate, mix } from 'framer-motion';
import UIIcon from 'UIComponents/icon/UIIcon';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import { MINIMAP_CONTAINER_WIDTH } from 'automation-ui-flow-core/canvas/graphConstants';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { isMinimapMinimized, getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import * as localStorageActions from 'automation-ui-canvas/actions/LocalStorageActions';
import * as LocalStorageKeys from 'automation-ui-canvas/constants/LocalStorageKeys';
import wireFlow from 'automation-ui-canvas/decorators/WiredFlowDecorator';
import UndoRedoContainer from 'automation-ui-canvas/containers/undo-redo/UndoRedoContainer';
import ZoomableButtonGroup from 'automation-ui-canvas/components/canvas/ZoomableButtonGroup';
import { Button } from 'foundations-components/transitional/components/Button';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_ZOOM = 1;
const MIN_ZOOM = 0.3;
const ZOOM_CHANGE = 0.1;
const StyledRootZoomable = styled.div.withConfig({
  displayName: "ZoomableDecorator__StyledRootZoomable",
  componentId: "cayn05-0"
})(["width:100%;transform-origin:0px 0px;"]);
const StyledZoomButtons = styled.div.withConfig({
  displayName: "ZoomableDecorator__StyledZoomButtons",
  componentId: "cayn05-1"
})(["display:flex;flex-direction:row;position:fixed;left:", ";z-index:1;"], ({
  isMinimized
}) => isMinimized ? '20px' : `${MINIMAP_CONTAINER_WIDTH + 28}px`);
const StyledSmallContainer = styled.div.withConfig({
  displayName: "ZoomableDecorator__StyledSmallContainer",
  componentId: "cayn05-2"
})(["display:flex !important;"]);
const StyledSmall = styled.small.withConfig({
  displayName: "ZoomableDecorator__StyledSmall",
  componentId: "cayn05-3"
})(["padding:", ";margin-top:auto;margin-bottom:auto;margin-left:", ";border-radius:", ";background:", ";box-shadow:", ";"], ({
  theme
}) => theme.space[100], ({
  theme
}) => theme.space[100], ({
  theme
}) => theme.borderRadius[200], ({
  $isMinimized
}) => $isMinimized ? 'none' : 'rgba(255, 255, 255, 0.8)', ({
  $isMinimized
}) => $isMinimized ? 'none' : '0 3px 10px rgb(0 0 0 / 0.2)');
/**
 * Cheat-sheet of referenced DOM elements:
 * - scrollElementRef: a React 16-style ref object; this component
 *     renders in a UIEditorPage, so scrollElementRef.current
 *     will be the first parent of the Canvas which has
 *    `overflow: auto`.
 * - rootZoomableNodeRef: the first `single-node` of the
 *     canvas, which should be the largest element in the
 *     zoomable.
 * - rootZoomableNodeRef.current.parentElement: the parent is needed
 *     because it will be smaller than its child in cases
 *     where the canvas is large.
 * - this.containerEl: the element the zoom scale is applied to
 */
export default function zoomDecorator({
  WrappedComponent,
  shouldRenderUndoRedoButtons = false
}) {
  class ZoomableDecorator extends Component {
    constructor(...args) {
      super(...args);
      this.containerRef = /*#__PURE__*/createRef();
      this.scrollPosition = {
        scrollLeft: 0,
        scrollTop: 0
      };
      this.zoomLevel = this.props.zoomLevel;
      this.animation = null;
      this.setZoomLevel = newZoomLevel => {
        const {
          flowId,
          mergeDeepWithLocalStorage,
          zoomLevel: oldZoomLevel
        } = this.props;
        trackEvent(Trackables.CLICKED_ZOOM_BUTTON, {
          destination: newZoomLevel > oldZoomLevel ? 'in' : 'out',
          size: Math.round(newZoomLevel * 100)
        });
        const portalId = PortalIdParser.get();
        mergeDeepWithLocalStorage({
          [`${LocalStorageKeys.ZOOM_LEVEL}-${portalId}-${flowId}`]: newZoomLevel
        });
      };
      this.onZoom = newZoomLevel => {
        this.setZoomLevel(Math.round(newZoomLevel * 100) / 100);
      };
      this.handleZoomIn = event => {
        event.stopPropagation();
        const newZoom = this.props.zoomLevel + ZOOM_CHANGE;
        this.onZoom(newZoom);
      };
      this.handleZoomOut = event => {
        event.stopPropagation();
        const newZoom = this.props.zoomLevel - ZOOM_CHANGE;
        this.onZoom(newZoom);
      };
    }
    componentDidUpdate(prevProps) {
      const {
        zoomLevel: newZoomLevel
      } = this.props;
      const {
        zoomLevel: oldZoomLevel
      } = prevProps;
      if (newZoomLevel !== oldZoomLevel) {
        var _this$animation;
        // this.zoomLevel is updated during the animation so it's more accurate
        // than prevProps.zoomLevel
        const lastAppliedZoomLevel = this.zoomLevel;
        const zoomLevelMixer = mix(lastAppliedZoomLevel, newZoomLevel);
        (_this$animation = this.animation) === null || _this$animation === void 0 || _this$animation.stop();

        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.animation = animate(0, 1, {
          duration: 0.2,
          ease: 'linear',
          onUpdate: delta => {
            this.applyZoomLevelChange(lastAppliedZoomLevel, zoomLevelMixer(delta));
          },
          onComplete: () => {
            this.animation = null;
          }
        });
      }
    }

    // Adjust the scroll position and outer margin to keep the workflow visually
    // centered at the top center when the scaling changes.
    applyZoomLevelChange(oldZoomLevel, newZoomLevel) {
      const {
        scrollElementRef
      } = this.props;
      const scrollContainer = scrollElementRef.current;
      const zoomContainer = this.containerRef.current;
      const parentEl = zoomContainer.parentElement;
      let newLeftScroll = 0;
      let newTopScroll = 0;
      let newMarginLeft = 0;
      const {
        scrollLeft,
        scrollTop
      } = this.scrollPosition;
      newTopScroll = scrollTop / oldZoomLevel * newZoomLevel;
      const isTooCloseToScroll = parentEl.offsetWidth === parentEl.scrollWidth;
      if (!isTooCloseToScroll) {
        newLeftScroll = (scrollLeft + scrollContainer.offsetWidth / 2) / oldZoomLevel * newZoomLevel - scrollContainer.offsetWidth / 2;
      } else {
        newMarginLeft = (parentEl.offsetWidth - zoomContainer.scrollWidth * newZoomLevel) / 2;
      }
      zoomContainer.style.transform = `scale(${newZoomLevel})`;
      zoomContainer.style.marginLeft = `${newMarginLeft}px`;
      scrollContainer.scrollLeft = newLeftScroll;
      scrollContainer.scrollTop = newTopScroll;
      this.zoomLevel = newZoomLevel;
    }
    formatZoomLevel(zoomLevel, formatPercentage = true) {
      const roundedZoom = Math.round(zoomLevel * 100);
      return formatPercentage ? `${roundedZoom}%` : roundedZoom;
    }
    renderZoomButtons() {
      const {
        isMinimized
      } = this.props;
      const {
        zoomLevel
      } = this.props;
      return /*#__PURE__*/_jsxs(StyledZoomButtons, {
        isMinimized: isMinimized,
        onMouseDown: event => event.stopPropagation(),
        children: [shouldRenderUndoRedoButtons && /*#__PURE__*/_jsx(UndoRedoContainer, {
          isMinimized: isMinimized,
          buttonTheme: "tertiary-extra-light"
        }), /*#__PURE__*/_jsxs(ZoomableButtonGroup, {
          isMinimized: isMinimized,
          children: [/*#__PURE__*/_jsx(Button, {
            padding: "200",
            use: "tertiary-extra-light",
            size: "medium",
            onClick: this.handleZoomIn,
            disabled: zoomLevel >= MAX_ZOOM,
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "add"
            })
          }), /*#__PURE__*/_jsx(Button, {
            padding: "200",
            use: "tertiary-extra-light",
            size: "medium",
            onClick: this.handleZoomOut,
            disabled: zoomLevel - ZOOM_CHANGE <= MIN_ZOOM,
            children: /*#__PURE__*/_jsx(UIIcon, {
              name: "minus"
            })
          })]
        }), /*#__PURE__*/_jsx(StyledSmallContainer, {
          children: /*#__PURE__*/_jsx(StyledSmall, {
            $isMinimized: isMinimized,
            children: this.formatZoomLevel(zoomLevel)
          })
        })]
      });
    }
    render() {
      const {
        scrollElementRef
      } = this.props;

      // record the scroll position prior to any layout changes caused by
      // rendering at a new zoom scale so that componentdidUpdate() can identify
      // the user's intended scroll position
      const scrollContainer = scrollElementRef && scrollElementRef.current;
      if (scrollContainer) {
        this.scrollPosition.scrollLeft = scrollContainer.scrollLeft;
        this.scrollPosition.scrollTop = scrollContainer.scrollTop;
      }
      return /*#__PURE__*/_jsxs("div", {
        children: [this.renderZoomButtons(), /*#__PURE__*/_jsx(StyledRootZoomable, {
          style: {
            transform: `scale(${this.zoomLevel || MAX_ZOOM})`
          },
          ref: this.containerRef,
          children: /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props))
        })]
      });
    }
  }

  // order is important
  ZoomableDecorator.displayName = `ZoomableDecorator(${getComponentName(WrappedComponent)})`;
  ZoomableDecorator.defaultProps = WrappedComponent.defaultProps;
  const ConnectedZoomableDecorator = compose(withRouter, wireFlow, connect((state, {
    flowId
  }) => {
    return {
      zoomLevel: getZoomLevel(flowId)(state),
      isMinimized: isMinimapMinimized(state)
    };
  }, {
    mergeDeepWithLocalStorage: localStorageActions.mergeDeepWithLocalStorage
  }))(ZoomableDecorator);
  attachWrappedComponent(ConnectedZoomableDecorator, WrappedComponent);
  return ConnectedZoomableDecorator;
}